import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ShopSettings, ShopSettingsRepository } from '@infrab4a/connect';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { FileControllerService } from 'src/app/admin-api';
import { CustomizationFormComponent } from 'src/app/components/customization/customization-form.component';
import { AppDialogService } from 'src/app/services/dialog.service';
import { ImageService } from 'src/app/services/image.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-customization-component-banner-form',
  templateUrl: './customization-component-banner-form.component.html',
  styleUrl: './customization-component-banner-form.component.scss'
})
export class CustomizationComponentBannerFormComponent
  extends CustomizationFormComponent
  implements OnInit
{
  pageDocument: ShopSettings;
  landingPageRoute: string;

  constructor(
    @Inject('ShopSettingsRepository')
    private shopSettingsRepository: ShopSettingsRepository,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private activatedRoute: ActivatedRoute,
    override router: Router,
    override imageService: ImageService,
    override config: PrimeNGConfig,
    override fileService: FileControllerService
  ) {
    super(router, config, imageService, fileService);

    this.form = this.formBuilder.group({
      desktopImage: ['', [Validators.required]],
      mobileImage: ['', [Validators.required]],
      title: ['', [Validators.required]],
      altText: [
        '',
        [
          Validators.required,
          Validators.minLength(50),
          Validators.maxLength(125)
        ]
      ],
      beginDate: ['', [Validators.required]]
    });
  }

  async ngOnInit() {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    this.documentId = this.activatedRoute.snapshot.paramMap
      .get('id')
      .toUpperCase();
    this.landingPageRoute = id.replaceAll('_', '-');
    await this.getDocument();
  }

  override async getDocument(): Promise<void> {
    try {
      LoaderService.showLoader();
      this.pageDocument = await this.shopSettingsRepository.get({
        id: this.documentId
      });
      this.documentSections = this.pageDocument.sections || [];
      if (this.router.url.includes('edit')) {
        this.activatedRoute.params.subscribe((params) => {
          this.activeIndex = +params['index'];
          this.activeSection = this.documentSections[this.activeIndex];
          this.initializeForm();
        });
      }
    } catch (error) {
      this.form.patchValue(this.activeSection);
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  override initializeForm(): void {
    this.form.patchValue(this.activeSection);
  }

  override async save() {
    if (this.form.valid) {
      const doc = this.form.value;

      if (
        doc.beginDate <= this.today &&
        (!doc.endDate || this.today <= new Date(doc.endDate))
      ) {
        doc.active = true;
      } else {
        doc.active = false;
      }

      if (this.activeSection) this.documentSections[this.activeIndex] = doc;
      else this.documentSections.push(doc);

      try {
        LoaderService.showLoader();
        await this.shopSettingsRepository.update({
          id: this.documentId,
          sections: this.documentSections
        });
        this.messageService.add({
          severity: 'success',
          summary: 'Salvo com sucesso',
          detail: 'As informações foram salvas.'
        });
        await this.afterSubmit();
        this.navigate(
          `/configuracoes/${
            this.landingPageRoute
          }/${this.activatedRoute.snapshot.paramMap.get('id')}`
        );
      } catch (err) {
        AppDialogService.showErrorDialog(err);
      } finally {
        LoaderService.showLoader(false);
      }
    } else {
      console.error('Formulário inválido. Não é possível salvar os dados.');
    }
  }

  isBeginDateInvalid() {
    const beginDateControl = this.form.get('beginDate');
    return (
      beginDateControl?.invalid &&
      (beginDateControl?.touched || beginDateControl?.dirty)
    );
  }

  isAltTextInvalid() {
    const altTextControl = this.form.get('altText');
    return (
      altTextControl?.invalid &&
      (altTextControl?.touched || altTextControl?.dirty)
    );
  }

  isTitleInvalid() {
    const titleControl = this.form.get('title');
    return (
      titleControl?.invalid && (titleControl?.touched || titleControl?.dirty)
    );
  }

  async handleFileSelect(
    event: { files: File[] },
    inputName: string,
    expectedWidth: number,
    expectedHeight: number,
    maxWeight: number,
    pInput: FileUpload
  ) {
    this.fileNamePattern = `customization-mensmarket-${new Date().getTime()}`;

    await this.upload(
      event,
      inputName,
      expectedWidth,
      expectedHeight,
      maxWeight,
      pInput
    );
  }

  goToMenu() {
    this.navigate(
      `configuracoes/${
        this.landingPageRoute
      }/${this.activatedRoute.snapshot.paramMap.get('id')}`
    );
  }

  get desktopBannerUrl(): string {
    return this.form.get('desktopImage')?.value;
  }

  get mobileBannerUrl(): string {
    return this.form.get('mobileImage')?.value;
  }

  get filePath(): string {
    return '/pages/lp-assinaturas/';
  }

  override get model() {
    return this.pageDocument;
  }

  override get modelId() {
    return this.pageDocument?.id;
  }
}
